import { get } from 'svelte/store'

import type { Passport } from '@src/Passport'
import { sdk } from '@lib/sdk/sdk'

import { Transaction } from '@mysten/sui/transactions'
import { SuiNetworks } from '@packages/utils/enums'
import { configStore } from '@src/stores/config'
import { PassportEvents } from '@packages/utils/lib/events/events.enums'
import { dispatch } from '@packages/utils/lib/events/events'
import { analytics } from '@packages/utils/lib/mixpanel/mixpanel'
import { type TSuiSendNftArgs } from '@src/utils/types'
import { MIST_PER_SUI } from '@mysten/sui/utils'
import { SUI_CRED_DECIMALS } from '@src/constants/blockchain'
import { convertSuiMicroBalance, getSuiCoinType, getSuiCredCoinType } from '@src/utils/blockchain'
import { AssetType } from '@pages/payment/payment.constants'
import { getSuiClient, initSuiClient } from '@src/libs/sui'
import { suiPackageStore } from '@packages/stores'

export async function getAddress() {
  return (await sdk.sui.getAddress()) ?? null
}

export async function switchChain(this: Passport, { suiNetworkName }: { suiNetworkName: SuiNetworks }) {
  sdk.sui.switchNetwork(suiNetworkName)
  this.configurePassport({ suiNetwork: suiNetworkName, config: this.config, clientId: this.config.clientId })
}

export async function checkMembership(membershipOwner: string, membershipPackage: string) {
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(membershipPackage, suiNetwork)

  const res = await client.CredenzaMembership.hasMembership(membershipOwner)

  return { isMember: !!res, meta: res || {} }
}

export async function sendNft({ packageId, recipient, assetId, amount = 1 }: TSuiSendNftArgs) {
  const { clientId, suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)
  let result

  if (assetId) {
    result = await client.CredenzaAssetCollection.sendAsset(+assetId, amount, recipient)
  } else {
    const ownerAddress = await getAddress()
    if (!ownerAddress) {
      throw new Error('No Sui address available')
    }
    const res = await client.getOwnedObjectsGrpc(ownerAddress, `${packageId}::decom::DecomToken`)
    if (!res.length) {
      throw new Error('No DecomToken found')
    }
    result = await client.CredenzaDecom.sendAsset(res[0].object_id, recipient)
  }

  const transferData = {
    suiNetwork,
    clientId,
    from: await getAddress(),
    to: recipient,
    assetId,
    amount,
  }
  dispatch(PassportEvents.SEND_NFT, { ...result, ...transferData })
  analytics.track('cp_transfer_nft', transferData)
  return result
}

export const getUserCollectionAssetsWithBalances = async (
  packageId: string,
  userAddess?: string,
): Promise<{ [assetId: string]: number }> => {
  let address = userAddess
  if (!address) {
    address = (await getAddress()) as string
  }
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)

  const assetsIds = await client.CredenzaAssetCollection.getAssetsIds()

  const assetsWithBalances: { [assetId: string]: number } = {}

  await Promise.all(
    assetsIds.map(async (assetId: number) => {
      const balance = await client.CredenzaAssetCollection.balanceOf(address, assetId)
      assetsWithBalances[assetId] = balance
    }),
  )

  return assetsWithBalances
}

export async function sendTokens({
  address,
  amount,
  isSuiNativeCoinTransfer = false,
}: {
  address: string
  amount: number
  isSuiNativeCoinTransfer: boolean
}) {
  try {
    if (!address) return
    if (isSuiNativeCoinTransfer) {
      await sendNativeTokens(address, amount)
    } else {
      await sendCredTokens(address, amount)
    }

    dispatch(PassportEvents.SEND_TOKENS)
    const { clientId, suiNetwork } = get(configStore)
    analytics.track('cp_transfer_tokens', {
      suiNetwork,
      clientId,
      coinType: isSuiNativeCoinTransfer ? getSuiCoinType() : getSuiCredCoinType(),
      from: await getAddress(),
      to: address,
      amount,
    })
  } catch (err) {
    dispatch(PassportEvents.ERROR, { error: err })
    throw err
  }
}

export const getBalance = async (userAddess?: string): Promise<string> => {
  let address = userAddess
  if (!address) {
    address = await getAddress()
  }
  if (!address) {
    throw new Error('No Sui address available')
  }
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(get(suiPackageStore), suiNetwork)
  const totalBalance = await client.getBalance(address, getSuiCredCoinType())

  return convertSuiMicroBalance(totalBalance, SUI_CRED_DECIMALS)
}

export const getNativeSuiBalance = async () => {
  const address = await getAddress()
  if (!address) {
    throw new Error('No Sui address available')
  }
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(get(suiPackageStore), suiNetwork)
  const totalBalance = await client.getBalance(address, getSuiCoinType())

  return convertSuiMicroBalance(totalBalance)
}

async function sendNativeTokens(address: string, amount: number) {
  const ownerAddress = await getAddress()
  if (!ownerAddress) {
    throw new Error('No Sui address available')
  }

  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(get(suiPackageStore), suiNetwork)
  const coinType = getSuiCoinType()
  const suiCoinObjectType = `0x2::coin::Coin<${coinType}>`
  const amountMist = Math.round(amount * Number(MIST_PER_SUI))
  const balance = await client.getBalance(ownerAddress, coinType)

  if (balance < BigInt(amountMist)) {
    throw new Error('Insufficient SUI balance')
  }

  const data = await client.getOwnedObjectsGrpc(ownerAddress, suiCoinObjectType)
  if (!data.length) {
    throw new Error('No SUI coins found')
  }

  const tx = new Transaction()
  const baseCoinId = data[0].object_id
  const baseCoinObj = tx.object(baseCoinId)
  if (data.length > 1) {
    const otherObjs = data.slice(1).map((ob: { object_id: string }) => tx.object(ob.object_id))
    tx.mergeCoins(baseCoinObj, otherObjs)
  }
  const [coin] = tx.splitCoins(baseCoinObj, [amountMist])
  tx.transferObjects([coin], address)

  return await client.signAndExecuteTransaction(tx)
}

async function sendCredTokens(address: string, amount: number) {
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(get(suiPackageStore), suiNetwork)
  return await client.CRED.sendCred(amount * SUI_CRED_DECIMALS, address)
}

export async function buyWithCred(
  type: string,
  packageId: string,
  collectionArgs?: { amount?: number; assetId?: number },
) {
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)
  const ownerAddress = await getAddress()

  const credClient = await initSuiClient(get(suiPackageStore), suiNetwork)
  const data = await client.getOwnedObjectsGrpc(ownerAddress, credClient.CRED.getCredCoinType())
  const tx = new Transaction()
  const baseCoinId = data[0].object_id
  const baseCoinObj = tx.object(baseCoinId)
  if (data.length > 1) {
    const otherObjs = data.slice(1).map((ob: { object_id: string }) => tx.object(ob.object_id))
    tx.mergeCoins(baseCoinObj, otherObjs)
  }
  await credClient.signAndExecuteTransaction(tx)
  await new Promise((resolve) => setTimeout(resolve, 1000))

  const { amount, assetId } = collectionArgs || {}

  if (type === AssetType.CREDENZA_ASSET_COLLECTION && assetId && amount) {
    return await client.CredenzaAssetCollection.buyAsset(baseCoinId, getSuiCredCoinType(), assetId, amount)
  } else if (type === AssetType.MEMBERSHIP) {
    return await client.CredenzaMembership.buyMembership(baseCoinId, getSuiCredCoinType())
  } else {
    return await client.CredenzaDecom.buyDecomToken(baseCoinId, getSuiCredCoinType())
  }
}

export { getSuiClient }

export async function fetchAssetMetadata(packageId: string, assetId: string) {
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)
  const metadata = await client.CredenzaAssetCollection.getAssetMetadata(Number(assetId))
  let assetImage = ''
  if (metadata?.meta_url) {
    try {
      const response = await fetch(metadata.meta_url)
      const metaJson = await response.json()
      assetImage = metaJson.image || metaJson.image_url || ''
    } catch (e) {
      console.warn('Failed to load meta_url image', e)
    }
  }
  return { ...metadata, assetImage }
}
